/**
 * Hook for fetching ultimate origination sources via recursive graph traversal
 *
 * Follows the DISCOVERED relationship chain all the way back to find the original
 * sources that led to the discovery of the current asset. Uses length: -1 to
 * traverse unlimited hops through the discovery chain.
 */

import { useGraphQuery } from '@/hooks/useGraphQuery';
import { TreeNode } from '@/types';

export interface UseUltimateOriginsQueryResult {
  /** Ultimate origins tree structure from graph query */
  data: TreeNode[] | undefined;
  /** True if query is in initial loading state */
  isLoading: boolean;
  /** Query status */
  status: 'pending' | 'error' | 'success';
}

/**
 * Custom hook for fetching ultimate origination sources
 *
 * Recursively traverses the DISCOVERED relationship chain backwards to find
 * the root sources that originally led to discovering this asset.
 *
 * Uses length: -1 for unlimited hop traversal, which means:
 * - Follows DISCOVERED relationships backwards from target asset
 * - Continues until no more parent discoveries exist
 * - Returns all assets in the origination chain
 *
 * @param assetKey - The asset key to find ultimate origins for
 * @returns Ultimate origins tree data with loading state
 */
export function useUltimateOriginsQuery(
  assetKey: string
): UseUltimateOriginsQueryResult {
  const { data, isLoading, status } = useGraphQuery({
    tree: true,
    query: {
      node: {
        relationships: [
          {
            label: 'DISCOVERED',
            length: -1, // Follow discovery chain all the way back to find ultimate source
            target: {
              labels: ['Asset'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: assetKey,
                },
              ],
            },
          },
        ],
      },
    },
  });

  return {
    data,
    isLoading,
    status,
  };
}
